import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterClaremont() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-claremont",
    name: "Painters Claremont",
    location: "Claremont, Cape Town",
    rating: 4.8,
    reviews: 178,
    specialties: ["Interior", "Exterior", "Heritage Homes", "Modern Properties"],
    image: "https://public.readdy.ai/ai/img_res/34903151f073c03d3367d262534f4601.jpg",
    verified: true,
    yearsExperience: 13,
    website: "https://paintersclaremont.co.za",
    email: "info@paintersclaremont.co.za",
    phone: "+27 21 555 0167",
    description: "Expert painting services in Claremont and southern suburbs. We specialize in both heritage home restoration and modern property painting with attention to architectural details and quality finishes that preserve character while enhancing beauty.",
    services: [
      "Heritage Home Restoration",
      "Interior House Painting", 
      "Exterior House Painting",
      "Victorian Home Painting",
      "Modern Property Painting",
      "Color Consultation",
      "Decorative Finishes",
      "Maintenance Painting"
    ],
    serviceAreas: ["Claremont", "Newlands", "Rondebosch", "Kenilworth", "Wynberg", "Southern Suburbs"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20heritage%20Victorian%20home%20interior%20painted%20by%20professional%20painters%20in%20Claremont%2C%20elegant%20period%20features%20with%20fresh%20paint%2C%20quality%20restoration%20work%2C%20traditional%20architecture%20with%20modern%20finishes&width=400&height=300&seq=claremont-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Heritage%20home%20exterior%20painting%20project%20in%20Claremont%20Cape%20Town%2C%20Victorian%20architecture%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20restoration%20finish%2C%20tree-lined%20suburban%20street%20setting&width=400&height=300&seq=claremont-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Modern%20family%20home%20interior%20painting%20in%20Claremont%2C%20contemporary%20design%20with%20fresh%20painted%20walls%2C%20quality%20paint%20finish%2C%20bright%20clean%20space%2C%20professional%20painting%20work&width=400&height=300&seq=claremont-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Elegant%20dining%20room%20interior%20painting%20in%20Claremont%20heritage%20home%2C%20period%20features%20with%20fresh%20paint%2C%20quality%20restoration%20work%2C%20traditional%20charm%20with%20modern%20finishes&width=400&height=300&seq=claremont-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Elizabeth Parker",
        rating: 5,
        comment: "Outstanding work on our Victorian home restoration. The team understood the heritage requirements and delivered exceptional results. Highly recommend for period properties."
      },
      {
        name: "Andrew Wilson", 
        rating: 5,
        comment: "Excellent painting services for our modern home. Professional team, quality materials, and attention to detail. Very satisfied with the final result."
      },
      {
        name: "Margaret Brown",
        rating: 4,
        comment: "Good quality work on our heritage home. The painters were respectful of the property's character and delivered a beautiful finish."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Claremont | Professional House Painting Services Cape Town | Residential Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Claremont provides professional painting services in Claremont, Cape Town. Specializing in residential painting, heritage properties, interior and exterior work. Quality local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Claremont, house painters Claremont, painting contractors Cape Town, residential painting services, heritage property painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20beautiful%20heritage%20home%20in%20Claremont%20Cape%20Town%2C%20tree-lined%20suburban%20street%2C%20painters%20in%20clean%20uniforms%20with%20quality%20brushes%20and%20equipment%2C%20well-maintained%20Victorian%20architecture%2C%20bright%20natural%20lighting%2C%20high%20quality%20residential%20painting%20restoration%20project&width=1200&height=600&seq=claremont-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About {painter.name}</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20residential%20home%20in%20Claremont%20Cape%20Town%2C%20southern%20suburbs%20property%20with%20quality%20paint%20finish%2C%20painters%20in%20uniform%20with%20professional%20equipment%2C%20traditional%20suburban%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=claremont-sidebar-image&orientation=landscape"
                      alt="Professional painters Claremont residential house painting services Cape Town southern suburbs"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Expert {service.toLowerCase()} services with quality materials and skilled craftsmanship.</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Project Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews</h2>
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and Southern Suburbs</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get a Quote</h3>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
